/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type {
  ChartPoint,
  ProgressChartModel,
} from '@sportninja/common/features/profile/stats/types'
import {
  buildValueTicks,
  pickTickIndexes,
} from '@sportninja/common/features/profile/stats/utils/axisTicks'
import {
  isFlatSeries,
  normalizeSeries,
} from '@sportninja/common/features/profile/stats/utils/normalizeSeries'
import {
  buildChartReadout,
  buildPointTooltip,
  formatChartReadout,
} from '@sportninja/common/features/profile/stats/utils/pointTooltip'
import { formatStatValue } from '@sportninja/common/features/profile/stats/utils/statValues'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { useId, useState } from 'react'
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLine,
  VictoryScatter,
} from 'victory'

import Text from 'src/components/ts-components/Text'

import { useElementWidth } from '../hooks/useElementWidth'
import { getChartScale } from '../utils/chartScale'
import { getYAxisInset } from '@sportninja/common/features/profile/stats/utils/yAxisInset'
import ChartPointTooltip from './ChartPointTooltip'
import GamesPlayedStrip, { GAMES_STRIP_HEIGHT } from './GamesPlayedStrip'
import ProgressPointMarker, { type PointHover } from './ProgressPointMarker'

/** One charted line. Every series covers the same periods. */
export interface ProgressChartSeries {
  /** Stat being charted ('GP' when the line itself is games played). */
  abbr: string
  /** Full stat name for the readout when several lines share the chart. */
  name?: string
  chart: ProgressChartModel
  color: string
}

interface ProgressChartProps {
  /**
   * One series draws on its own value axis. Two or more overlay, each scaled
   * to its own domain, with an unlabelled y-axis — real values show in the
   * readout.
   */
  series: ProgressChartSeries[]
  /**
   * Chart heading. Omitted when the selected stat chip already names what the
   * line shows.
   */
  heading?: string
  /** Colour of the games-played bars under the lines; no bars without it. */
  gamesStripColor?: string
}

interface ChartDatum {
  x: number
  y: number | null
  /** Where the area under the line ends: the bottom of the plot. */
  y0: number
}

/** A hovered point and the line it belongs to. */
interface SeriesHover extends PointHover {
  seriesIndex: number
}

// Sizes at full scale. The chart draws at true pixel size on a wide screen
// (the viewBox matches the container), so text and points stop growing with
// the window, and shrinks in proportion only below CHART_REFERENCE_WIDTH.
const CHART_HEIGHT = 280
const FALLBACK_WIDTH = 720
// `left` is sized to the y-axis labels per render (`getYAxisInset`).
const CHART_PADDING = { top: 16, bottom: 32, right: 16 }
/** Space between a y-axis label and the gridline it labels. */
const Y_LABEL_GAP = 8
const DEFAULT_READOUT = 'Select a point for details'
/** Normalized lines span 0..1; a small margin keeps the dots off both edges. */
const MULTI_Y_DOMAIN: [number, number] = [-0.05, 1.05]
/** Gridlines only (bottom, middle, top) — normalized values mean nothing. */
const MULTI_Y_TICKS = [0, 0.5, 1]
/**
 * Area fill under each line: the line colour fading to clear at the bottom.
 * Overlaid lines start fainter so their areas don't muddy each other.
 */
const AREA_TOP_OPACITY = 0.6
const MULTI_AREA_TOP_OPACITY = 0.3

// SVG text otherwise takes Victory's built-in font; `inherit` picks up the
// tenant font from the page. Victory styles take numbers, not css.js presets.
const TICK_LABEL_STYLE = {
  fill: colors.NEUTRAL_50,
  fontSize: 12,
  fontFamily: 'inherit',
}

const blankTick = (): string => ''

/**
 * Trend lines for the charted stats, each over a fading area fill; empty
 * periods of a rate stat are gaps.
 */
const ProgressChart = ({
  series,
  heading,
  gamesStripColor,
}: ProgressChartProps) => {
  'use memo'

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [hovered, setHovered] = useState<SeriesHover | null>(null)
  const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>()
  // Gradient ids are document-global; keep them unique per chart instance.
  // useId's colons would break the `url(#…)` fill reference.
  const gradientPrefix = useId().replace(/:/g, '')

  const scale = getChartScale(containerWidth)
  // ViewBox width in chart units: the container's width at full scale, wider
  // (so everything draws smaller) once the scale drops below 1.
  const viewBoxWidth =
    containerWidth > 0 ? containerWidth / scale : FALLBACK_WIDTH
  const toPercent = (units: number): string =>
    `${(units / viewBoxWidth) * 100}%`

  const first = series[0]
  if (!first) {
    return null
  }
  const isMulti = series.length > 1
  // Without the bar strip the chart takes over its height, so the block stays
  // the same size instead of shrinking. The strip is HTML (unscaled px) and
  // the chart is drawn at `scale`, hence the division.
  const chartHeight = gamesStripColor
    ? CHART_HEIGHT
    : CHART_HEIGHT + GAMES_STRIP_HEIGHT / scale
  // Every series shares the periods, so the first one drives the x-axis.
  const { points, domain } = first.chart
  const areaBase = isMulti ? MULTI_Y_DOMAIN[0] : domain.min
  const areaTopOpacity = isMulti ? MULTI_AREA_TOP_OPACITY : AREA_TOP_OPACITY
  const lines = series.map((s, index) => {
    const plotted = isMulti ? normalizeSeries(s.chart) : s.chart.points
    const data: ChartDatum[] = plotted.map((p) => ({
      x: p.index,
      y: p.value,
      y0: areaBase,
    }))
    return {
      ...s,
      data,
      gradientId: `${gradientPrefix}area-${index}`,
      // Overlaid, a stat that never changes sits mid-chart; its fill would be
      // a solid band over the others, so it draws as a line only.
      showArea: !isMulti || !isFlatSeries(s.chart),
    }
  })
  const xTicks = pickTickIndexes(points.length)
  const yTicks = isMulti
    ? MULTI_Y_TICKS
    : buildValueTicks(domain.min, domain.max, first.abbr)
  const formatYTick = isMulti
    ? blankTick
    : (value: number) => formatStatValue(value, first.abbr)
  // Only as much room on the left as the y labels need, so the plot uses the
  // card's width with the same margin on both sides.
  const padding = {
    ...CHART_PADDING,
    left: getYAxisInset(
      yTicks.map((tick) => formatYTick(tick)),
      Y_LABEL_GAP,
      CHART_PADDING.right
    ),
  }
  const formatReadout = (point: ChartPoint): string =>
    formatChartReadout(buildChartReadout(point, series))
  const selectedPoint =
    selectedIndex === null ? undefined : points[selectedIndex]
  const labelFor = (index: number): string => {
    const point = points[index]
    return point ? formatReadout(point) : DEFAULT_READOUT
  }
  // Victory hands points their position in viewBox units; the SVG is drawn at
  // `scale`, so the same factor converts them to px for the HTML tooltip.
  const hoveredSeries = hovered ? series[hovered.seriesIndex] : undefined
  const hoveredPoint = hovered
    ? hoveredSeries?.chart.points[hovered.index]
    : undefined

  return (
    <div css={wrapStyles}>
      {heading ? (
        <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(heading)}
        </Text>
      ) : null}
      <div aria-live='polite'>
        <Text
          variant='paragraphSmall'
          weight='medium'
          color={colors.NEUTRAL_50}
        >
          {selectedPoint ? formatReadout(selectedPoint) : DEFAULT_READOUT}
        </Text>
      </div>
      <div ref={containerRef} css={plotStyles}>
        {/* Victory has no slot for <defs>; a zero-size SVG holds the area
            gradients, which the chart's fills reference by id. */}
        <svg css={gradientDefsStyles} aria-hidden='true' focusable='false'>
          <defs>
            {lines.map((line) => (
              <linearGradient
                key={line.gradientId}
                id={line.gradientId}
                x1='0'
                y1='0'
                x2='0'
                y2='1'
              >
                <stop
                  offset='0%'
                  stopColor={line.color}
                  stopOpacity={areaTopOpacity}
                />
                <stop offset='100%' stopColor={line.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
        </svg>
        <VictoryChart
          width={viewBoxWidth}
          height={chartHeight}
          padding={padding}
          // Half a period either side centres each point over its strip bar.
          domain={{
            x: [-0.5, points.length - 0.5],
            y: isMulti ? MULTI_Y_DOMAIN : [domain.min, domain.max],
          }}
        >
          <VictoryAxis
            tickValues={xTicks}
            tickFormat={(i: number) => points[i]?.label ?? ''}
            style={{
              axis: { stroke: colors.SOFT_STEEL },
              tickLabels: TICK_LABEL_STYLE,
            }}
          />
          <VictoryAxis
            dependentAxis
            // Pin the axis to the plot's left edge. Left to itself it sits at
            // x = 0, half a period inside the plot, and its labels land on
            // top of the gridlines.
            offsetX={padding.left}
            tickValues={yTicks}
            tickFormat={formatYTick}
            style={{
              axis: { stroke: 'transparent' },
              grid: { stroke: colors.SOFT_STEEL, strokeDasharray: '4,4' },
              tickLabels: { ...TICK_LABEL_STYLE, padding: Y_LABEL_GAP },
            }}
          />
          {/* Victory reads its children as a flat array; fragments break it. */}
          {lines.flatMap((line, index) => [
            ...(line.showArea
              ? [
                  <VictoryArea
                    key={`${line.abbr}:area`}
                    data={line.data}
                    style={{ data: { fill: `url(#${line.gradientId})` } }}
                  />,
                ]
              : []),
            <VictoryLine
              key={`${line.abbr}:line`}
              data={line.data}
              style={{ data: { stroke: line.color, strokeWidth: 2 } }}
            />,
            <VictoryScatter
              key={`${line.abbr}:points`}
              data={line.data.filter((d) => d.y !== null)}
              dataComponent={
                <ProgressPointMarker
                  selectedIndex={selectedIndex}
                  onSelect={setSelectedIndex}
                  labelFor={labelFor}
                  color={line.color}
                  // One tab stop per period: only the first line's points
                  // take focus; the readout covers every line.
                  focusable={index === 0}
                  onHoverChange={(hover) =>
                    setHovered(hover ? { ...hover, seriesIndex: index } : null)
                  }
                />
              }
            />,
          ])}
        </VictoryChart>
        {hovered && hoveredSeries && hoveredPoint ? (
          <ChartPointTooltip
            tooltip={buildPointTooltip(
              hoveredPoint,
              hoveredSeries.abbr,
              hoveredSeries.name
            )}
            color={hoveredSeries.color}
            left={hovered.x * scale}
            top={hovered.y * scale}
          />
        ) : null}
        {gamesStripColor ? (
          <GamesPlayedStrip
            points={points}
            accent={gamesStripColor}
            insetLeft={toPercent(padding.left)}
            insetRight={toPercent(padding.right)}
          />
        ) : null}
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/** Positioning context for the hover tooltip. */
const plotStyles = css`
  position: relative;
`

const gradientDefsStyles = css`
  position: absolute;
  width: 0;
  height: 0;
`

const wrapStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export default ProgressChart
